import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import CallsUtils from "Utils/CallsUtils";
import DateUtils from "Utils/DateUtils";
import ConstantObj from "Static/constant";
import NotesTab from "./NotesTab";
import AiNotesTab from "./AiNotesTab";
import TranscriptTab from "./TranscriptTab";
import AudioPlayer from "./AudioPlayer";

const TABS = [
  { id: "notes", label: "Overview" },
  { id: "aiNotes", label: "AI Notes" },
  { id: "transcript", label: "Transcript" }
];

const getFormattedDate = (createTs = "") => {
  if (!createTs) return "--";
  const d = new Date(createTs);
  const hour = DateUtils.getHourValue(d.getHours());
  const minutes = DateUtils.getMinutesValue(d.getMinutes());
  const meridian = DateUtils.getMerdians(d.getHours());
  const date = `${ConstantObj?.months?.[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  const time = `${hour}:${minutes} ${meridian}`;
  return `${date} · ${time}`;
};

const OUTCOME_CLASSES = {
  connected: "outcomeTagConnected",
  "no answer": "outcomeTagNoAnswer",
  "left voicemail": "outcomeTagVoicemail",
  "not interested": "outcomeTagNotInterested",
  "wrong number": "outcomeTagWrongNumber"
};

const CallDetailPanel = observer(({ call = {} }) => {
  const {
    id = "",
    contactName = "",
    memberName = "",
    outcome = "",
    duration = 0,
    createTs = "",
    recordingUrl = "",
    notes = "",
    callSid = "",
    toNumber = ""
  } = call;

  const [activeTab, setActiveTab] = useState("notes");

  useEffect(() => {
    setActiveTab("notes");
  }, [id]);

  const hasAiNotes = !!callSid;
  const outcomeClass =
    OUTCOME_CLASSES[outcome?.toLowerCase()] || "outcomeTagDefault";
  const formattedDateTime = getFormattedDate(createTs);
  const formattedDuration = CallsUtils.secondsToTime(duration);

  if (!id) {
    return (
      <div className="callDetailPanel callDetailPanelEmpty">
        <div className="callDetailPanelEmptyIcon">
          <i className="material-icons-outlined">phone_in_talk</i>
        </div>
        <p className="callDetailPanelEmptyText">Select a call to view details</p>
      </div>
    );
  }

  return (
    <div className="callDetailPanel">
      <div className="callDetailPanelHeader">
        <div className="callDetailPanelHeaderTop">
          <div className="callDetailPanelNameBlock">
            <span className="callDetailPanelContactName">{contactName || toNumber || "--"}</span>
            <div className="callDetailPanelMetaRow">
              <span className="callDetailPanelMetaItem">
                <i className="material-icons-outlined">schedule</i>
                {formattedDuration}
              </span>
              <span className="callDetailPanelMetaItem">
                <i className="material-icons-outlined">person</i>
                {memberName || "--"}
              </span>
              <span className="callDetailPanelMetaItem">
                <i className="material-icons-outlined">calendar_today</i>
                {formattedDateTime}
              </span>
              {hasAiNotes && (
                <span className="callDetailPanelAiBadge">
                  <i className="material-icons">stars</i>
                  AI Notes
                </span>
              )}
            </div>
          </div>
          <span className={`outcomeTag ${outcomeClass}`}>{outcome || "--"}</span>
        </div>

        {recordingUrl && (
          <div className="callDetailPanelRecordingBlock">
            <AudioPlayer src={recordingUrl} totalDuration={duration} />
            <div className="callDetailPanelRecordingActions"></div>
          </div>
        )}
      </div>

      <div className="callDetailPanelTabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`callDetailPanelTab${activeTab === tab.id ? " callDetailPanelTabActive" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.id === "aiNotes" && hasAiNotes && (
              <span className="callDetailPanelTabDot" />
            )}
            {tab.id === "transcript" && recordingUrl && (
              <span className="callDetailPanelTabDot" />
            )}
          </button>
        ))}
      </div>

      <div className="callDetailPanelBody">
        {activeTab === "notes" && <NotesTab call={call} />}
        {activeTab === "aiNotes" && <AiNotesTab call={call} />}
        {activeTab === "transcript" && <TranscriptTab call={call} />}
      </div>
    </div>
  );
});

export default CallDetailPanel;
